/**
 * AreaShiftTable — Areas by Shift breakdown (Day/Evening/Night).
 */

import { useMemo } from 'react';
import HeaderInfoTip from '../../../../components/v2/HeaderInfoTip.jsx';

const AREAS_BY_SHIFT_TOOLTIP = [
    'This table breaks down the average daily alerts and average response time by area type for each shift over the last 28 days.',
    'Avg Daily is the average number of alerts per day occurring during that shift.',
    'Avg Resp is the average time, in minutes, from when an alert was triggered to when a caregiver acknowledged it.',
    'Shift hours follow your campus shift schedule when configured, otherwise the system defaults are used.',
];

/* ── Shift hour range formatting ── */
const formatHour = (h) => {
    const norm = ((h % 24) + 24) % 24;
    if (norm === 0) return '12am';
    if (norm === 12) return '12pm';
    if (norm < 12) return `${norm}am`;
    return `${norm - 12}pm`;
};

const buildShiftRange = (hourShift, shiftName) => {
    if (!hourShift || !shiftName) return '';
    const hours = Object.entries(hourShift)
        .filter(([, name]) => name === shiftName)
        .map(([h]) => parseInt(h, 10))
        .filter((h) => !isNaN(h))
        .sort((a, b) => a - b);

    if (hours.length === 0) return '';

    // Build contiguous segments
    const segments = [];
    let segStart = hours[0];
    let prev = hours[0];
    for (let i = 1; i < hours.length; i++) {
        if (hours[i] === prev + 1) {
            prev = hours[i];
        } else {
            segments.push([segStart, prev]);
            segStart = hours[i];
            prev = hours[i];
        }
    }
    segments.push([segStart, prev]);

    // Wrap-around: e.g. Night shift covering 22,23,0,1,2,3 → two segments [0..3] and [22..23]
    if (segments.length === 2 && segments[0][0] === 0 && segments[1][1] === 23) {
        return `${formatHour(segments[1][0])} - ${formatHour((segments[0][1] + 1) % 24)}`;
    }

    return segments.map(([s, e]) => `${formatHour(s)} - ${formatHour((e + 1) % 24)}`).join(', ');
};

const AreaShiftTable = ({ data, hourChartData }) => {
    const rows = Array.isArray(data) ? data : [];

    const shiftRanges = useMemo(() => {
        const hourShift = hourChartData?.hourShift || {};
        return {
            day:     buildShiftRange(hourShift, 'Day'),
            evening: buildShiftRange(hourShift, 'Evening'),
            night:   buildShiftRange(hourShift, 'Night'),
        };
    }, [hourChartData]);

    const renderShiftLabel = (name, range) => (
        <span className="md-shift-label">
            {name}
            {range ? <span style={{ marginLeft: '6px', fontWeight: 'var(--font-weight-normal)', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>({range})</span> : null}
        </span>
    );

    return (
        <div className="md-section">
            <h3 className="md-section__title" style={{ marginBottom: 'var(--space-4)' }}>
                Areas by Shift
                <HeaderInfoTip title="About This Table" body={AREAS_BY_SHIFT_TOOLTIP.join(' ')} />
            </h3>
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th className="dp-th" rowSpan={2}>Area Type</th>
                            <th className="dp-th md-shift--day dp-th--center" colSpan={2}>
                                {renderShiftLabel('Day', shiftRanges.day)}
                            </th>
                            <th className="dp-th md-shift--evening dp-th--center" colSpan={2}>
                                {renderShiftLabel('Evening', shiftRanges.evening)}
                            </th>
                            <th className="dp-th md-shift--night dp-th--center" colSpan={2}>
                                {renderShiftLabel('Night', shiftRanges.night)}
                            </th>
                        </tr>
                        <tr>
                            <th className="dp-th md-shift--day dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Daily</th>
                            <th className="dp-th md-shift--day dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Resp (min)</th>
                            <th className="dp-th md-shift--evening dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Daily</th>
                            <th className="dp-th md-shift--evening dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Resp (min)</th>
                            <th className="dp-th md-shift--night dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Daily</th>
                            <th className="dp-th md-shift--night dp-th--center" style={{ fontSize: 'var(--font-size-xs)' }}>Avg Resp (min)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr className="dp-tr"><td className="dp-td dp-td--center" colSpan={7}><span className="dp-cell-text dp-cell-text--muted">No shift data</span></td></tr>
                        ) : null}
                        {rows.map((r, i) => (
                            <tr key={i} className="dp-tr">
                                <td className="dp-td" style={{ fontWeight: r.area_type_name === 'Totals' ? 600 : 'normal' }}>{r.area_type_name || '—'}</td>
                                <td className="dp-td md-shift--day dp-td--center">{r.avg_daily_events_1st ?? '-'}</td>
                                <td className="dp-td md-shift--day dp-td--center">{r.avg_response_time_1st ?? '-'}</td>
                                <td className="dp-td md-shift--evening dp-td--center">{r.avg_daily_events_2nd ?? '-'}</td>
                                <td className="dp-td md-shift--evening dp-td--center">{r.avg_response_time_2nd ?? '-'}</td>
                                <td className="dp-td md-shift--night dp-td--center">{r.avg_daily_events_3rd ?? '-'}</td>
                                <td className="dp-td md-shift--night dp-td--center">{r.avg_response_time_3rd ?? '-'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AreaShiftTable;
